import React, { useCallback, useState } from "react";
import { Button } from "./ui/button";
import { StarRating } from "./ui/star-rating";
import { Badge } from "./ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Pencil, Trash2, Copy, Plus, Star, Share2, Camera, ChevronLeft, ChevronRight, AlertTriangle, Info, Printer, GripVertical } from "lucide-react";
import ShareOptionsDialog from "./ShareOptionsDialog";
import type { ShareOptions } from "./ShareOptionsDialog";
import { useDropzone } from "react-dropzone";
import { Link, useParams } from "react-router";
import _ from "lodash";
import { cn } from "@/lib/utils";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

import useProjectActions from "../hooks/useProjectActions";
import useFiringActions from "../hooks/useFiringActions";
import useSharedActions from "../hooks/useSharedActions";
import useFirebaseKiln from "../hooks/useFirebaseKiln";
import { findSuitablePhoto } from "../helpers/photoHelpers";
import useFirebaseProject from "../hooks/useFirebaseProject";
import useFirebaseFirings from "../hooks/useFirebaseFirings";
import useFirebasePreferences from "../hooks/useFirebasePreferences";
import useFirebaseGlassData from "../hooks/useFirebaseGlassData";
import useFirebaseSegments from "../hooks/useFirebaseSegments";

import { useAuth } from "../helpers/Auth";
import useSharedFirebaseProject from "../hooks/useSharedFirebaseProject";
import useSharedFirebaseFirings from "../hooks/useSharedFirebaseFirings";
import useSharedFirebaseSegments from "../hooks/useSharedFirebaseSegments";

import { glassImage } from "../helpers/logoHelpers";
import { getReactingMaterials, getHexColor, getGlassReactionTypeCharacters, getGlassReactionTypeColour } from "../helpers/glassHelpers";
import { convertLengthUnit } from "../helpers/unitHelpers";
import { getProjectWarnings } from "../helpers/warningHelpers";
import type { Firing } from "../types";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import FiringCard from "./FiringCard";
import PageHeader from "./PageHeader";
import { makeScaryURLQuery, decodeScaryURLQueryParameter } from "../helpers/shareHelpers";

const PhotoGallery = ({ photos, slideIndex, setSlideIndex, onFavourite, onOpenLightbox, deleteLink, readOnly }: { photos: any; slideIndex: any; setSlideIndex: any; onFavourite: any; onOpenLightbox: any; deleteLink: any; readOnly: any }) => {
  const hasPrev = slideIndex > 0;
  const hasNext = slideIndex < photos.length - 1;
  const hasThumbs = photos.length > 1;

  return (
    <div className="max-w-[800px]">
      <div
        className={`relative overflow-hidden border border-border ${hasThumbs ? "rounded-t-xl border-b-0" : "rounded-xl"}`}
        style={{ background: "linear-gradient(135deg, #2b3b4f, #5b7f9f)" }}
      >
        <img
          src={findSuitablePhoto(photos[slideIndex], "medium").uri}
          alt=""
          className="mx-auto cursor-pointer max-h-[500px] object-contain block"
          onClick={onOpenLightbox}
        />
        <div className="absolute top-2 left-2">
          <button
            type="button"
            className="p-1.5 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            onClick={() => onFavourite(slideIndex)}
          >
            <Star
              className={`h-5 w-5 ${
                photos[slideIndex]?.isFavourite
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-white"
              }`}
            />
          </button>
        </div>
        {!readOnly && (
          <div className="absolute top-2 right-2">
            <Link to={deleteLink}>
              <button
                type="button"
                className="p-1.5 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
              >
                <Trash2 className="h-5 w-5 text-white" />
              </button>
            </Link>
          </div>
        )}
        {hasPrev && (
          <button
            type="button"
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            onClick={() => setSlideIndex(slideIndex - 1)}
          >
            <ChevronLeft className="h-6 w-6 text-white" />
          </button>
        )}
        {hasNext && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            onClick={() => setSlideIndex(slideIndex + 1)}
          >
            <ChevronRight className="h-6 w-6 text-white" />
          </button>
        )}
        <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
          <span className="text-white text-lg drop-shadow-md">
            {photos[slideIndex]?.type}
          </span>
        </div>
      </div>
      {hasThumbs && (
        <div className="flex gap-1 p-2 bg-muted border border-border rounded-b-xl overflow-x-auto">
          {photos.map((photo: any, i: any) => (
            <button
              key={i}
              type="button"
              className={`shrink-0 rounded overflow-hidden border-2 transition-colors ${
                i === slideIndex ? "border-primary" : "border-transparent hover:border-border"
              }`}
              onClick={() => setSlideIndex(i)}
            >
              <img
                src={findSuitablePhoto(photo, "small").uri}
                alt=""
                className="h-[60px] w-[80px] object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MaterialRow = React.forwardRef<HTMLDivElement, {
  material: any;
  materialId: string;
  projectId: string;
  defaultGlass: string;
  reacting: boolean;
  readOnly: boolean;
  isDragging?: boolean;
  draggableProps?: any;
  dragHandleProps?: any;
}>(({ material, materialId, projectId, defaultGlass, reacting, readOnly, isDragging, draggableProps, dragHandleProps }, ref) => {
  const glass_type = material.glass || defaultGlass;
  const glass_data = useFirebaseGlassData(glass_type);
  const inventory = glass_data?.inventory;
  const glass_ref = material.glass_reference;

  let description = material.description || "";
  let color: any = null;
  if (inventory && glass_ref && inventory[glass_ref]) {
    description = inventory[glass_ref].description + (material.description ? " - " + material.description : "");
    color = inventory[glass_ref].color;
  }

  const characters = glass_data && glass_ref ? getGlassReactionTypeCharacters(glass_ref, glass_data) : "";
  const reactionColour = glass_data && glass_ref ? getGlassReactionTypeColour(glass_ref, glass_data) : "";

  return (
    <div
      ref={ref}
      {...draggableProps}
      className={cn(
        "group flex items-center gap-2.5 px-3 py-2 rounded-lg border border-border bg-card text-sm mb-1.5",
        reacting && "bg-destructive/5 border-destructive/30"
      )}
      style={{
        background: isDragging ? "var(--color-muted)" : undefined,
        ...draggableProps?.style,
      }}
    >
      {!readOnly && (
        <div
          {...dragHandleProps}
          className="cursor-grab text-muted-foreground hover:text-foreground shrink-0"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      )}
      {color?.multicolored ? (
        <span className="h-5 w-5 shrink-0 rounded-sm border border-border bg-gradient-to-br from-pink-400 via-yellow-300 to-sky-400" />
      ) : color ? (
        <span
          className="h-5 w-5 shrink-0 rounded-sm border border-border"
          style={{ backgroundColor: getHexColor(color.rgb) }}
        />
      ) : (
        <span className="h-5 w-5 shrink-0 rounded-sm border border-border bg-muted" />
      )}
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate font-medium">{description || "—"}</div>
        {(glass_ref || reacting) && (
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            {glass_ref && <span className="font-mono">{glass_ref}</span>}
            {reacting && <span className="text-destructive font-semibold">REACTS</span>}
          </div>
        )}
      </div>
      {characters && (
        <span
          className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
          style={{ backgroundColor: reactionColour }}
          title={characters}
        >
          {characters}
        </span>
      )}
      {!readOnly && (
        <div className="shrink-0 flex gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <Link to={`/edit_material/${projectId}/${materialId}`}>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Pencil className="h-3 w-3" />
            </Button>
          </Link>
          <Link to={`/delete_material/${projectId}/${materialId}`}>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive">
              <Trash2 className="h-3 w-3" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
});

const ProjectShowPage = () => {
  const { id, sharing_user_id: sharing_uid, shared_project_id } = useParams();
  const { effectiveUID: uid, fakeUID } = useAuth();

  const [copied, setCopied] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { editProject, editPending: upload_pending, duplicateProject } = useProjectActions();
  const { editFiring } = useFiringActions();
  const { createSharedProject, createSharedFiring, createSharedSegment } = useSharedActions();
  let project, firings, segments, kiln;
  const firebase_project = useFirebaseProject(id!);
  const firebase_kiln = useFirebaseKiln(firebase_project?.kiln!);
  const all_firings = useFirebaseFirings();
  const all_segments = useFirebaseSegments();

  const shared_project = useSharedFirebaseProject(sharing_uid!, shared_project_id!);
  const all_shared_firings = useSharedFirebaseFirings(sharing_uid!);
  const all_shared_segments = useSharedFirebaseSegments(sharing_uid!);

  const handleShare = (options: ShareOptions) => {
    const sharedProject = _.cloneDeep(project) as any;
    delete sharedProject.sale_status;
    delete sharedProject.material_cost;
    delete sharedProject.firing_cost;
    delete sharedProject.labour_cost;
    delete sharedProject.sale_price;
    delete sharedProject.sold_to;

    if (sharedProject.photos) {
      sharedProject.photos = sharedProject.photos.filter((p: any) => {
        const type = (p.type || "").toLowerCase();
        if (type === "before") return options.includeBeforePhotos;
        if (type === "during") return options.includeDuringPhotos;
        if (type === "after") return options.includeAfterPhotos;
        return true;
      });
      if (sharedProject.photos.length === 0) {
        delete sharedProject.photos;
      }
    }

    if (!options.includeMaterials) {
      delete sharedProject.materials;
    }

    createSharedProject(sharedProject);

    const selectedFirings = (firings || []).filter(
      (f: any) => f?.id && options.selectedFiringIds.includes(f.id)
    );
    selectedFirings.forEach((firing: any) => createSharedFiring(firing));

    const selectedFiringIds = new Set(options.selectedFiringIds);
    const selectedSegments = (segments || []).filter(
      (s: any) => s?.firing_id && selectedFiringIds.has(s.firing_id)
    );
    selectedSegments.forEach((segment: any) => createSharedSegment(segment));

    navigator.clipboard.writeText(
      `https://kilnhelper.web.app/shared_project/${uid}/${project!.id}`
    );
    setCopied(true);
    setShareDialogOpen(false);
  };

  if (id) {
    firings = _.filter(all_firings, (firing: any) => (firing && firing.project_id) === id);
    segments = firings?.reduce(
      (acc: any[], firing: any) =>
        acc.concat(_.filter(all_segments, (segment: any) => segment?.firing_id === firing.id)),
      []
    );
    project = firebase_project;
    kiln = firebase_kiln;
  } else {
    project = shared_project;
    firings = _.filter(
      all_shared_firings,
      (firing: any) => (firing && firing.project_id) === shared_project_id
    );
    segments = firings?.reduce(
      (acc: any[], firing: any) =>
        acc.concat(_.filter(all_shared_segments, (segment: any) => segment?.firing_id === firing.id)),
      []
    );

    kiln = {};
  }
  const readOnly = !id;

  const preferences = useFirebasePreferences();
  const glass_data = useFirebaseGlassData(project?.glass!);


  let firings_array;
  if (firings) {
    firings_array = Object.values(firings).sort((a: any, b: any) => {
      return (a.order ?? 0) - (b.order ?? 0);
    });
  }
  let materials_key_array;
  if (project?.materials) {
    materials_key_array = Object.keys(project.materials).sort((a, b) => {
      return ((project.materials as any)[a].order ?? 0) - ((project.materials as any)[b].order ?? 0);
    });
  }

  const onDrop = useCallback((accepted_files: any) => {
    const newProject = _.cloneDeep(project) as any;
    newProject.photos = newProject?.photos
      ? newProject.photos.concat(accepted_files.map((p: any) => ({ photo: p, type: "After" })))
      : accepted_files.map((p: any) => ({ photo: p, type: "After" }));
    editProject(project!.id!, newProject, true);
  }, [project, editProject]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/jpeg': [], 'image/png': [] } as any,
    onDrop,
  });

  if (!kiln || !project) {
    return (
      <div className="space-y-6">
        <PageHeader title="Project" breadcrumbs={[{ label: "Projects", to: "/projects" }, { label: "Loading..." }]} />
        <div className="h-8 w-1/3 bg-muted animate-pulse rounded" />
        <div className="h-48 bg-muted animate-pulse rounded" />
      </div>
    );
  }
  const length_unit = project.length_unit || "mm";
  const desired_unit = (preferences && preferences.length_unit) || "mm";
  const width = convertLengthUnit(length_unit, desired_unit, project.width ?? 0);
  const depth = convertLengthUnit(length_unit, desired_unit, project.depth ?? 0);
  const thickness = convertLengthUnit(length_unit, desired_unit, project.thickness ?? 0);

  const lightboxSlides =
    project.photos &&
    project.photos.map((photo: any) => ({
      src: findSuitablePhoto(photo, "large").uri as string,
    }));

  const favouriteClicked = (index: any) => {
    const newProject = _.cloneDeep(project);
    if (newProject.photos?.[index].isFavourite) {
      newProject.photos[index].isFavourite = false;
    } else {
      newProject.photos = newProject.photos?.map((photo: any) => ({
        ...photo,
        isFavourite: false,
      })) ?? [];
      newProject.photos[index].isFavourite = true;
    }

    editProject(project.id!, newProject, true);
  };
  const newOrder = Math.max(...(firings_array ?? []).map((s: any) => s.order || 0), 0) + 1;
  const material_new_order = materials_key_array
    ? Math.max(...materials_key_array.map((s) => (project.materials as any)?.[s]?.order || 0), 0) + 1
    : 0;

  const reacting_materials = getReactingMaterials(project.materials as any, glass_data as any);

  const warnings = getProjectWarnings(
    project,
    firings_array as Firing[] ?? [],
    segments ?? [],
    glass_data
  );

  const hasPhotos = !!(project.photos && project.photos.length > 0);
  const hasFinancials = !!(project.material_cost || project.firing_cost || project.labour_cost || project.sale_price || project.sold_to);

  const headerActions = !readOnly ? (
    <>
      <Link to={`/projects/edit/${id}`}>
        <Button variant="outline" size="sm">
          <Pencil className="mr-1 inline-block h-4 w-4" />
          Edit
        </Button>
      </Link>
      {id && !fakeUID && (
        <Button variant="outline" size="sm" onClick={() => setShareDialogOpen(true)}>
          <Share2 className="mr-1 inline-block h-4 w-4" />
          Share
        </Button>
      )}
    </>
  ) : undefined;

  return (
    <div>
      <PageHeader
        title={project.name || "Untitled Project"}
        breadcrumbs={id ? [{ label: "Projects", to: "/projects" }, { label: project.name || "Project" }] : undefined}
        actions={headerActions}
      />

      {warnings.length > 0 && (() => {
        const hasWarning = warnings.some((w) => w.severity === "warning");
        const containerCls = hasWarning
          ? "mb-6 rounded-lg border border-amber-300 bg-amber-50"
          : "mb-6 rounded-lg border border-blue-300 bg-blue-50";
        return (
          <Accordion type="multiple" className={containerCls}>
            {warnings.map((warning, index) => {
              const isInfo = warning.severity === "info";
              const itemBorder = isInfo ? "border-blue-300" : "border-amber-300";
              const triggerCls = isInfo ? "px-4 text-blue-800 hover:no-underline" : "px-4 text-amber-800 hover:no-underline";
              const contentCls = isInfo ? "px-4 text-blue-700" : "px-4 text-amber-700";
              return (
                <AccordionItem key={index} value={`warning-${index}`} className={itemBorder}>
                  <AccordionTrigger className={triggerCls}>
                    <span className="flex items-center gap-2">
                      {isInfo
                        ? <Info className="h-4 w-4 text-blue-600" />
                        : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                      {warning.title}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className={contentCls}>
                    {warning.detail}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6 min-w-0">
          {hasPhotos ? (
            <>
              <PhotoGallery
                photos={project.photos}
                slideIndex={slideIndex}
                setSlideIndex={setSlideIndex}
                onFavourite={favouriteClicked}
                onOpenLightbox={() => setLightboxOpen(true)}
                deleteLink={`/projects/delete_photo/${id}/${slideIndex}`}
                readOnly={readOnly}
              />
              <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                index={slideIndex}
                on={{ view: ({ index }) => setSlideIndex(index) }}
                plugins={[Thumbnails, Zoom]}
              />
            </>
          ) : (
            <div
              className="aspect-[4/3] rounded-xl border border-border"
              style={{ background: "linear-gradient(135deg, #2b3b4f, #5b7f9f)" }}
            />
          )}

          {!readOnly && !hasPhotos && (
            upload_pending ? (
              <Button variant="outline" disabled>Uploading...</Button>
            ) : (
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Button variant="outline">
                  <Camera className="mr-1 inline-block h-4 w-4" />
                  {isDragActive ? "Drop Photo Here" : "Add Photo"}
                </Button>
              </div>
            )
          )}

          {project.notes && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{project.notes}</p>
          )}

          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-semibold">Firings</h3>
              {!readOnly && (
                <div className="flex gap-2">
                  <Link to={`/new_firing/${id}/${newOrder}`}>
                    <Button variant="outline" size="sm">
                      <Plus className="mr-1 inline-block h-3.5 w-3.5" />
                      New Firing
                    </Button>
                  </Link>
                  <Link to={`/new_favourite_firing/${id}`}>
                    <Button variant="outline" size="sm">
                      <Star className="mr-1 inline-block h-3.5 w-3.5" />
                      Favourite
                    </Button>
                  </Link>
                </div>
              )}
            </div>
            {firings_array && firings_array.length > 0 ? (
              !readOnly ? (
                <DragDropContext
                  onDragEnd={(result: any) => {
                    if (!result.destination) return;
                    const reordered = Array.from(firings_array as Firing[]);
                    const [removed] = reordered.splice(result.source.index, 1);
                    reordered.splice(result.destination.index, 0, removed);
                    reordered.forEach((firing, idx) => {
                      if ((firing.order ?? 0) !== idx) {
                        editFiring(firing.id!, { ...firing, order: idx }, false);
                      }
                    });
                  }}
                >
                  <Droppable droppableId="firings_list">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-2">
                        {firings_array.map(
                          (firing: any, index) =>
                            firing && (
                              <Draggable key={firing.id} draggableId={firing.id} index={index}>
                                {(dragProvided, snapshot) => (
                                  <FiringCard
                                    {...firing}
                                    index={index}
                                    to={`/firings/${firing.id}`}
                                    showHandle
                                    ref={dragProvided.innerRef}
                                    draggableProps={dragProvided.draggableProps}
                                    dragHandleProps={dragProvided.dragHandleProps}
                                    isDragging={snapshot.isDragging}
                                  />
                                )}
                              </Draggable>
                            )
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div className="flex flex-col gap-2">
                  {firings_array.map(
                    (firing: any, index) =>
                      firing && (
                        <FiringCard
                          key={firing.id}
                          {...firing}
                          index={index}
                          to={`/shared_firing/${sharing_uid}/${firing.id}`}
                        />
                      )
                  )}
                </div>
              )
            ) : (
              <div className="text-sm text-muted-foreground px-3.5 py-2.5 border border-dashed border-border rounded-lg">
                No firings yet.
              </div>
            )}
          </section>
        </div>

        <div className="space-y-6 min-w-0">
          <div className="bg-card border border-border rounded-xl p-[18px] shadow-sm">
            <h2 className="text-lg font-semibold mb-1">{project.name || "Untitled"}</h2>
            {kiln.name && (
              <div className="flex justify-between text-sm py-1.5 border-b border-dashed border-border">
                <span className="text-muted-foreground">Kiln</span>
                <span>{kiln.name}</span>
              </div>
            )}
            <div className="flex justify-between items-center text-sm py-1.5 border-b border-dashed border-border">
              <span className="text-muted-foreground">Glass</span>
              <span className="flex items-center gap-1.5">
                <img className="h-4 w-4 rounded-full object-cover bg-white border border-border" src={glassImage(project.glass)} alt="" />
                {project.glass}
              </span>
            </div>
            {(project.width || project.depth || project.thickness) && (
              <div className="flex justify-between text-sm py-1.5 border-b border-dashed border-border">
                <span className="text-muted-foreground">Size</span>
                <span className="font-mono tabular-nums">{`${width} \u00d7 ${depth} \u00d7 ${thickness} ${desired_unit}`}</span>
              </div>
            )}
            {(project.stars ?? 0) > 0 && (
              <div className="flex justify-between items-center text-sm py-1.5 border-b border-dashed border-border">
                <span className="text-muted-foreground">Rating</span>
                <StarRating maxRating={5} rating={project.stars} />
              </div>
            )}
            {project.tags && project.tags.length > 0 && (
              <div className="flex justify-between items-start gap-3 text-sm py-1.5 border-b border-dashed border-border">
                <span className="text-muted-foreground shrink-0">Tags</span>
                <div className="flex flex-wrap gap-1 justify-end">
                  {project.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
            {project.sale_status && (
              <div className="flex justify-between items-center text-sm py-1.5 border-b border-dashed border-border">
                <span className="text-muted-foreground">Status</span>
                <Badge
                  variant={
                    project.sale_status === "Destroyed" ? "destructive" :
                    project.sale_status === "Not For Sale" ? "secondary" :
                    "outline"
                  }
                  className={
                    project.sale_status === "Sold" ? "border-green-600 text-green-700" :
                    project.sale_status === "For Sale" ? "border-blue-600 text-blue-700" :
                    project.sale_status === "Gifted" ? "border-purple-600 text-purple-700" :
                    ""
                  }
                >
                  {project.sale_status}
                </Badge>
              </div>
            )}
            {hasFinancials && (
              <div className="mt-2 pt-2 border-t border-border">
                {project.material_cost && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-muted-foreground">Material Cost</span>
                    <span className="font-mono tabular-nums">{project.material_cost}</span>
                  </div>
                )}
                {project.firing_cost && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-muted-foreground">Firing Cost</span>
                    <span className="font-mono tabular-nums">{project.firing_cost}</span>
                  </div>
                )}
                {project.labour_cost && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-muted-foreground">Labour Cost</span>
                    <span className="font-mono tabular-nums">{project.labour_cost}</span>
                  </div>
                )}
                {project.sale_price && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-muted-foreground">Sale Price</span>
                    <span className="font-mono tabular-nums">{project.sale_price}</span>
                  </div>
                )}
                {project.sold_to && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-muted-foreground">Sold To</span>
                    <span>{project.sold_to}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-semibold">Materials</h3>
              {!readOnly && (
                <Link to={`/new_material/${id}/${material_new_order}`}>
                  <Button variant="outline" size="sm">
                    <Plus className="mr-1 inline-block h-3.5 w-3.5" />
                    Add
                  </Button>
                </Link>
              )}
            </div>
            {materials_key_array && materials_key_array.length > 0 ? (
              !readOnly ? (
                <DragDropContext
                  onDragEnd={(result: any) => {
                    if (!result.destination) return;
                    const reordered = Array.from(materials_key_array);
                    const [removed] = reordered.splice(result.source.index, 1);
                    reordered.splice(result.destination.index, 0, removed);
                    const updatedMaterials = { ...project.materials };
                    reordered.forEach((key, index) => {
                      (updatedMaterials as any)[key] = { ...(updatedMaterials as any)[key], order: index };
                    });
                    editProject(project.id!, { ...project, materials: updatedMaterials }, true);
                  }}
                >
                  <Droppable droppableId="materials_list">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {materials_key_array.map(
                          (key, index) =>
                            (project.materials as any)?.[key] && (
                              <Draggable key={key} draggableId={key} index={index}>
                                {(provided, snapshot) => (
                                  <MaterialRow
                                    ref={provided.innerRef}
                                    draggableProps={provided.draggableProps}
                                    dragHandleProps={provided.dragHandleProps}
                                    isDragging={snapshot.isDragging}
                                    material={(project.materials as any)[key]}
                                    materialId={key}
                                    projectId={project.id!}
                                    defaultGlass={project.glass!}
                                    reacting={typeof reacting_materials[key] !== "undefined"}
                                    readOnly={false}
                                  />
                                )}
                              </Draggable>
                            )
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div>
                  {materials_key_array.map(
                    (key) =>
                      (project.materials as any)?.[key] && (
                        <MaterialRow
                          key={key}
                          material={(project.materials as any)[key]}
                          materialId={key}
                          projectId={project.id!}
                          defaultGlass={project.glass!}
                          reacting={typeof reacting_materials[key] !== "undefined"}
                          readOnly={true}
                        />
                      )
                  )}
                </div>
              )
            ) : (
              <div className="text-sm text-muted-foreground px-3.5 py-2.5 border border-dashed border-border rounded-lg">
                No materials yet.
              </div>
            )}
          </section>
        </div>
      </div>

      {!readOnly && hasPhotos && (
        <div className="mt-6">
          {upload_pending ? (
            <Button variant="outline" disabled>Uploading...</Button>
          ) : (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <Button variant="outline">
                <Camera className="mr-1 inline-block h-4 w-4" />
                {isDragActive ? "Drop Photo Here" : "Add Photo"}
              </Button>
            </div>
          )}
        </div>
      )}

      {!readOnly && (
        <div className="mt-6 pt-6 border-t border-border flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const segmentsForProject = firings_array
                ? Object.values(all_segments || {}).filter(s => firings_array.some((f: any) => f.id === s.firing_id))
                : [];
              duplicateProject(project, firings_array as Firing[] || [], segmentsForProject);
            }}
          >
            <Copy className="mr-1 inline-block h-4 w-4" />
            Duplicate
          </Button>
          <Link to={`/projects/print/${id}`}>
            <Button variant="outline" size="sm">
              <Printer className="mr-1 inline-block h-4 w-4" />
              Print
            </Button>
          </Link>
          <Link to={`/projects/delete/${id}`} className="ml-auto">
            <Button variant="destructive" size="sm">
              <Trash2 className="mr-1 inline-block h-4 w-4" />
              Delete
            </Button>
          </Link>
        </div>
      )}

      {readOnly && (
        <div className="mt-6">
          {uid ? (
            <Button variant="outline">
              <Copy className="mr-1 inline-block h-4 w-4" />
              Store a copy in your own List of Projects
            </Button>
          ) : (
            <div className="text-sm text-muted-foreground">
              If you were logged in to Kiln Helper, you would be able to save a copy of this. To log
              in, you need to have purchased a copy of the Kilnhelper app on either the Apple or
              Google Play App Stores.
            </div>
          )}
        </div>
      )}

      {id && !fakeUID && (
        <>
          {copied && (
            <p className="mt-3 text-sm text-success-foreground font-medium">Link Copied to Clipboard</p>
          )}
          <ShareOptionsDialog
            open={shareDialogOpen}
            onOpenChange={setShareDialogOpen}
            project={project}
            firings={firings_array as Firing[] ?? []}
            segments={segments ?? []}
            onShare={handleShare}
          />
        </>
      )}
      {id && fakeUID && (
        <p className="mt-3 text-sm text-muted-foreground italic">Sharing is not available while impersonating a user.</p>
      )}
    </div>
  );
};

export default ProjectShowPage;
